import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LaunchIcon from '@mui/icons-material/Launch'
import LinkIcon from '@mui/icons-material/Link'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import HistoryIcon from '@mui/icons-material/History'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import PermissionCheck from '../../../helpers/PermissionCheck'
import AttachmentPreviewModal from '../../../components/Attachments/AttachmentPreviewModal'
import EmployeeSelector from '../../events/EmployeeSelector'
import SopBuilder from '../../registration/StatementOfPurpose/SopBuilder'
import ActivitiesList from '../../registration/Activities/ActivitiesList'

// Questions answered by an embedded feature rather than a saved response.
const EMBEDDED_ANSWER_TYPES = ['sop-builder', 'activities-tracker']

const STATUS_CHIP = {
  not_answered: { label: 'Not answered', color: 'error' },
  overdue: { label: 'Review overdue', color: 'error' },
  due_soon: { label: 'Review due soon', color: 'warning' },
  current: { label: 'Answered', color: 'success' },
}

const APPLIES_TO_LABEL = {
  both: null, // the default - no chip needed
  tddi: 'Treatment (TDDI)',
  pts: 'Transport (PTS)',
}

const LINK_ROUTE = {
  policy: (refId) => `/policy-library/${refId}`,
  sop: (refId) => `/standard-operating-procedures/${refId}`,
  crewvault: (refId) => `/crewvault/${refId}`,
  risk: (refId) => `/risk-register/${refId}`,
}

const LINK_KIND_LABEL = {
  policy: 'Policy',
  sop: 'SOP',
  crewvault: 'Crew Vault',
  risk: 'Risk',
  document: 'Document',
}

const HISTORY_EVENT_LABEL = {
  answered: 'Answered',
  updated: 'Answer updated',
  reviewed: 'Reviewed - still accurate',
}

// One expandable card per compliance question: the regulation context, the
// current answer (free text + linked records + attachments), review status
// and the full audit history of how the answer has been maintained.
const QuestionCard = ({
  question,
  linkOptions,
  expanded,
  onToggle,
  onSaved,
  onEmbeddedChange,
  focusRef,
}) => {
  const { setToast } = useContext(GlobalContext)
  const navigate = useNavigate()
  const [answerText, setAnswerText] = useState(question.response?.answerText ?? '')
  const [links, setLinks] = useState(question.response?.links ?? [])
  const [attachments, setAttachments] = useState(question.response?.attachments ?? [])
  const [employees, setEmployees] = useState(question.response?.employees ?? [])
  const [saving, setSaving] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [previewKey, setPreviewKey] = useState(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)

  const isEmbedded = EMBEDDED_ANSWER_TYPES.includes(question.answerType)
  const isEmployee = question.answerType === 'employee'

  useEffect(() => {
    setAnswerText(question.response?.answerText ?? '')
    setLinks(question.response?.links ?? [])
    setAttachments(question.response?.attachments ?? [])
    setEmployees(question.response?.employees ?? [])
  }, [question.response])

  const dirty =
    answerText !== (question.response?.answerText ?? '') ||
    JSON.stringify(links.map((l) => `${l.kind}:${l.refId}`)) !==
      JSON.stringify((question.response?.links ?? []).map((l) => `${l.kind}:${l.refId}`)) ||
    JSON.stringify(attachments.map((a) => a.s3key)) !==
      JSON.stringify((question.response?.attachments ?? []).map((a) => a.s3key)) ||
    JSON.stringify(employees.map((e) => e._id)) !==
      JSON.stringify((question.response?.employees ?? []).map((e) => e._id))

  const hasContent = Boolean(
    answerText.trim() || links.length || attachments.length || employees.length,
  )
  const status = STATUS_CHIP[question.reviewStatus] ?? STATUS_CHIP.not_answered

  const handleSave = () => {
    setSaving(true)
    API.put(`/cqc-centre/compliance/${question.key}`, {
      answerText,
      links,
      attachments,
      employees: employees.map((e) => e._id),
    })
      .then((res) => {
        if (res.status === 200) {
          setToast?.({ message: 'Answer saved' })
          onSaved(res.data)
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setSaving(false))
  }

  const handleMarkReviewed = () => {
    setSaving(true)
    API.post(`/cqc-centre/compliance/${question.key}/review`, {})
      .then((res) => {
        if (res.status === 200) {
          setToast?.({ message: 'Marked as reviewed' })
          onSaved(res.data)
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setSaving(false))
  }

  const handleUpload = async (file) => {
    if (!file) return
    setUploading(true)
    try {
      const urlRes = await API.post('/gets3uploadUrl', {
        folder: 'cqc-compliance',
        extension: file.type.split('/')[1] || 'pdf',
      })
      if (urlRes.status !== 200) return
      const putRes = await API.put(urlRes.data.url, file, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (putRes.status === 200) {
        setAttachments((prev) => [...prev, { fileName: file.name, s3key: urlRes.data.key }])
      }
    } catch (e) {
      console.log(e)
    } finally {
      setUploading(false)
    }
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      ref={focusRef}
      disableGutters
      sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          gap={1}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          sx={{ width: '100%', pr: 1 }}
        >
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {question.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {question.regulation} - {question.regulationTitle}
              {question.reviewDue &&
                question.reviewStatus !== 'not_answered' &&
                ` - review ${question.reviewStatus === 'overdue' ? 'was due' : 'due'} ${moment(question.reviewDue).format('Do MMM YYYY')}`}
            </Typography>
          </Box>
          <Stack direction="row" gap={0.5} alignItems="center" flexShrink={0}>
            {APPLIES_TO_LABEL[question.appliesTo] && (
              <Chip size="small" variant="outlined" label={APPLIES_TO_LABEL[question.appliesTo]} />
            )}
            <Chip size="small" color={status.color} label={status.label} />
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={2}>
          <Typography variant="body2" fontWeight={600}>
            {question.question}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {question.guidance}
          </Typography>
          {question.evidenceHints?.length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                Evidence an inspector would expect:
              </Typography>
              <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                {question.evidenceHints.map((hint) => (
                  <li key={hint}>
                    <Typography variant="caption" color="text.secondary">
                      {hint}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}
          {question.featureLink && (
            <Box>
              <Button
                size="small"
                variant="outlined"
                endIcon={<LaunchIcon />}
                onClick={() => navigate(question.featureLink.path)}
              >
                {question.featureLink.label}
              </Button>
            </Box>
          )}

          <Divider />

          {question.contextNote && (
            <Alert
              severity={question.answered ? 'success' : 'warning'}
              variant="outlined"
              sx={{ py: 0 }}
            >
              {question.contextNote}
            </Alert>
          )}

          {question.answerType === 'sop-builder' && (
            <SopBuilder embedded onChanged={onEmbeddedChange} />
          )}
          {question.answerType === 'activities-tracker' && (
            <ActivitiesList embedded onChanged={onEmbeddedChange} />
          )}

          {isEmployee && (
            <EmployeeSelector
              label={question.employeeLabel || 'Select person'}
              multiple={!!question.employeeMultiple}
              value={question.employeeMultiple ? employees : (employees[0] ?? null)}
              onChange={(e, value) =>
                setEmployees(
                  question.employeeMultiple ? (value ?? []) : value ? [value] : [],
                )
              }
              fullWidth
              size="small"
            />
          )}

          {!isEmbedded && (
            <TextField
              label={isEmployee ? 'Supporting notes (optional)' : 'Your answer'}
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              multiline
              minRows={isEmployee ? 2 : 3}
              size="small"
              fullWidth
              placeholder={
                isEmployee
                  ? 'Cover arrangements, check dates, or anything an inspector should know about this appointment.'
                  : 'Describe your current position - what is in place, where it is documented, and who owns it.'
              }
            />
          )}

          {!isEmbedded && (
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            {links.map((link) => (
              <Chip
                key={`${link.kind}:${link.refId}`}
                size="small"
                icon={<LinkIcon />}
                label={`${LINK_KIND_LABEL[link.kind]}: ${link.label || 'Linked record'}`}
                onClick={
                  LINK_ROUTE[link.kind]
                    ? () => navigate(LINK_ROUTE[link.kind](link.refId))
                    : undefined
                }
                onDelete={() =>
                  setLinks((prev) =>
                    prev.filter((l) => !(l.kind === link.kind && l.refId === link.refId)),
                  )
                }
              />
            ))}
            {attachments.map((attachment) => (
              <Chip
                key={attachment.s3key}
                size="small"
                icon={<AttachFileIcon />}
                label={attachment.fileName || 'Attachment'}
                onClick={() => setPreviewKey(attachment.s3key)}
                onDelete={() =>
                  setAttachments((prev) => prev.filter((a) => a.s3key !== attachment.s3key))
                }
              />
            ))}
          </Stack>
          )}

          {!isEmbedded && (
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ sm: 'center' }}>
            <Autocomplete
              options={linkOptions}
              groupBy={(option) => LINK_KIND_LABEL[option.kind]}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.kind === value.kind && option.refId === value.refId
              }
              value={null}
              blurOnSelect
              onChange={(e, value) => {
                if (!value) return
                setLinks((prev) =>
                  prev.some((l) => l.kind === value.kind && l.refId === value.refId)
                    ? prev
                    : [...prev, { kind: value.kind, refId: value.refId, label: value.label }],
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Link a policy, SOP, vault card or risk"
                  size="small"
                />
              )}
              sx={{ minWidth: 280, flex: 1 }}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                handleUpload(e.target.files?.[0])
                e.target.value = ''
              }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? 'Uploading…' : 'Attach file'}
            </Button>
          </Stack>
          )}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={1}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
          >
            <Stack direction="row" gap={1} alignItems="center">
              <Button
                size="small"
                startIcon={<HistoryIcon />}
                onClick={() => setShowHistory((v) => !v)}
                disabled={!question.response?.history?.length}
              >
                History ({question.response?.history?.length ?? 0})
              </Button>
              {question.response?.lastReviewedAt && (
                <Typography variant="caption" color="text.secondary">
                  Last reviewed {moment(question.response.lastReviewedAt).format('Do MMM YYYY')}
                  {question.response.lastReviewedBy?.firstName
                    ? ` by ${question.response.lastReviewedBy.firstName} ${question.response.lastReviewedBy.lastName}`
                    : ''}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" gap={1}>
              {question.answered && (isEmbedded || !dirty) && (
                <Tooltip title="Confirms the answer is still accurate and restarts the annual review clock">
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<TaskAltIcon />}
                    onClick={handleMarkReviewed}
                    disabled={saving}
                  >
                    Mark reviewed
                  </Button>
                </Tooltip>
              )}
              {!isEmbedded && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !hasContent || !dirty}
                >
                  {saving ? 'Saving…' : 'Save answer'}
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showHistory}>
            <Stack gap={0.5} sx={{ pt: 1 }}>
              {[...(question.response?.history ?? [])].reverse().map((entry, i) => (
                <Typography key={i} variant="caption" color="text.secondary">
                  {moment(entry.at).format('Do MMM YYYY, HH:mm')} -{' '}
                  {HISTORY_EVENT_LABEL[entry.event] || entry.event}
                  {entry.by?.firstName ? ` by ${entry.by.firstName} ${entry.by.lastName}` : ''}
                  {entry.note ? ` - "${entry.note}"` : ''}
                </Typography>
              ))}
            </Stack>
          </Collapse>
        </Stack>
      </AccordionDetails>
      <AttachmentPreviewModal
        open={previewKey}
        onClose={() => setPreviewKey(null)}
        s3key={previewKey}
        onDelete={null}
      />
    </Accordion>
  )
}

const ComplianceQuestions = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [linkOptions, setLinkOptions] = useState([])
  const [searchParams, setSearchParams] = useSearchParams()
  const [expandedKey, setExpandedKey] = useState(searchParams.get('focus'))
  const focusRefs = useRef({})

  const loadQuestions = () => {
    API.get('/cqc-centre/compliance')
      .then((res) => {
        if (res.status === 200) setData(res.data)
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadQuestions()
    // Linkable records: policies (latest version id so deep links resolve),
    // SOPs, crew vault cards and risks.
    Promise.all([
      API.get('/policies/library'),
      API.get('/sops'),
      API.get('/crewvaultcards'),
    ]).then(([policiesRes, sopsRes, vaultRes]) => {
      const options = []
      if (policiesRes.status === 200) {
        for (const description of policiesRes.data?.policies ?? []) {
          const latest = description.policies?.[0]
          if (latest?._id) {
            options.push({ kind: 'policy', refId: latest._id, label: description.policyTitle })
          }
        }
      }
      if (sopsRes.status === 200) {
        for (const sop of sopsRes.data ?? []) {
          options.push({ kind: 'sop', refId: sop._id, label: sop.title })
        }
      }
      if (vaultRes.status === 200) {
        for (const card of vaultRes.data ?? []) {
          options.push({ kind: 'crewvault', refId: card._id, label: card.title })
        }
      }
      setLinkOptions(options)
    })
  }, [])

  // Scroll the ?focus= question into view once loaded.
  useEffect(() => {
    const focus = searchParams.get('focus')
    if (focus && data && focusRefs.current[focus]) {
      setExpandedKey(focus)
      setTimeout(() => {
        focusRefs.current[focus]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 200)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleSaved = (updatedQuestion) => {
    setData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => (q.key === updatedQuestion.key ? updatedQuestion : q)),
    }))
  }

  const summary = useMemo(() => {
    if (!data) return null
    const total = data.questions.length
    const answered = data.questions.filter((q) => q.answered).length
    const overdue = data.questions.filter((q) => q.reviewStatus === 'overdue').length
    const dueSoon = data.questions.filter((q) => q.reviewStatus === 'due_soon').length
    return { total, answered, overdue, dueSoon }
  }, [data])

  if (loading) return <LinearProgress />
  if (!data) {
    return <Alert severity="error">Unable to load the compliance questions right now.</Alert>
  }

  const activityChips = []
  if (data.activityProfile?.tddi) activityChips.push('Treatment of disease, disorder or injury')
  if (data.activityProfile?.pts) activityChips.push('Transport services (PTS)')

  return (
    <PermissionCheck roles={['Governance']} showAlert>
      <Stack gap={2}>
        <Box>
          <Typography variant="title1">CQC Compliance Questions</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            A regulation-by-regulation self-assessment covering the 2014 Fundamental Standards,
            the 2009 Registration Regulations and CQC's independent-ambulance inspection focus
            areas. Answers must be reviewed annually - overdue reviews appear as gaps and
            required actions until refreshed.
          </Typography>
          <Stack direction="row" gap={1} sx={{ mt: 1 }} flexWrap="wrap">
            {activityChips.map((label) => (
              <Chip key={label} size="small" color="anBlue" variant="outlined" label={label} />
            ))}
            {!data.activityProfile?.derivedFromRegistration && (
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Showing questions for both regulated activities - record your activities in the
                Registration Centre to tailor this list.
              </Typography>
            )}
          </Stack>
        </Box>

        {summary && (
          <Alert
            severity={summary.overdue > 0 ? 'error' : summary.answered < summary.total ? 'warning' : 'success'}
          >
            {summary.answered} of {summary.total} questions answered
            {summary.overdue > 0 && ` - ${summary.overdue} overdue for annual review`}
            {summary.dueSoon > 0 && ` - ${summary.dueSoon} due for review within 30 days`}
            {summary.answered === summary.total && summary.overdue === 0 && summary.dueSoon === 0
              ? ' - all current. These answers feed your readiness score and the Inspection Pack.'
              : '. Unanswered and overdue questions appear as gaps in your readiness score.'}
          </Alert>
        )}

        {data.sections.map((section) => {
          const sectionQuestions = data.questions.filter((q) => q.section === section.key)
          if (sectionQuestions.length === 0) return null
          const sectionAnswered = sectionQuestions.filter(
            (q) => q.answered && q.reviewStatus !== 'overdue',
          ).length
          return (
            <Box key={section.key}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                sx={{ mb: 0.5 }}
              >
                <Box>
                  <Typography variant="title2">{section.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {section.description}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" flexShrink={0} sx={{ ml: 2 }}>
                  {sectionAnswered}/{sectionQuestions.length} current
                </Typography>
              </Stack>
              <Stack gap={1}>
                {sectionQuestions.map((question) => (
                  <QuestionCard
                    key={question.key}
                    question={question}
                    linkOptions={linkOptions}
                    expanded={expandedKey === question.key}
                    onToggle={(e, isExpanded) => {
                      setExpandedKey(isExpanded ? question.key : null)
                      if (searchParams.get('focus')) {
                        setSearchParams({}, { replace: true })
                      }
                    }}
                    onSaved={handleSaved}
                    onEmbeddedChange={loadQuestions}
                    focusRef={(el) => {
                      focusRefs.current[question.key] = el
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )
        })}

        <Alert severity="info" variant="outlined">
          These questions reflect the regulations and CQC guidance at the time they were written
          and are a practical self-assessment aid - not legal advice, a CQC rating, or a
          guarantee of compliance. Always check current CQC guidance.
        </Alert>
      </Stack>
    </PermissionCheck>
  )
}

export default ComplianceQuestions
